// IdentityItem.tsx
import React, { useState } from 'react';
import { Check, X, AlertCircle, Clock, CheckCircle, XCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import type { IdentityVerification } from '../../types';
import { MEDIA_BASE_URL } from '../../api/auth';

const rejectionReasons = [
    { value: 'blurry', label: 'Image too blurry/unclear' },
    { value: 'name_mismatch', label: 'Name does not match account' },
    { value: 'expired', label: 'ID appears expired' },
    { value: 'incomplete', label: 'ID information incomplete' },
    { value: 'wrong_type', label: 'Wrong ID type submitted' },
    { value: 'suspicious', label: 'Document appears altered/suspicious' },
    { value: 'other', label: 'Other (specify)' }
];

interface IdentityItemProps {
    item: IdentityVerification;
    onVerify: (id: number, approved: boolean, reason?: string) => Promise<void>;
    onClick: () => void;
    showStatusBadge?: boolean;
}

const IdentityItem: React.FC<IdentityItemProps> = ({
    item,
    onVerify,
    onClick,
    showStatusBadge = false
}) => {
    const [showRejectOptions, setShowRejectOptions] = useState(false);
    const [selectedReason, setSelectedReason] = useState('');
    const [customReason, setCustomReason] = useState('');
    const [isVerifying, setIsVerifying] = useState(false);

    const isPending = item.id_verification_status === 'pending';
    const isApproved = item.id_verification_status === 'approved';
    const isRejected = item.id_verification_status === 'rejected';

    const imageUrl = `${MEDIA_BASE_URL}/id_documents/${item.id_document_path}`;

    const renderStatusBadge = () => {
        if (!showStatusBadge) return null;

        const styles = {
            pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
            approved: 'bg-green-100 text-green-800 border-green-200',
            rejected: 'bg-red-100 text-red-800 border-red-200'
        };

        const icons = {
            pending: <Clock size={14} className="mr-1" />,
            approved: <CheckCircle size={14} className="mr-1" />,
            rejected: <XCircle size={14} className="mr-1" />
        };

        return (
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${styles[item.id_verification_status]}`}>
                {icons[item.id_verification_status]}
                {item.id_verification_status}
            </span>
        );
    };

    const handleApprove = async () => {
        setIsVerifying(true);
        try {
            await onVerify(item.id, true);
            toast.success('Identity approved');
        } catch (error) {
            toast.error('Failed to approve');
        } finally {
            setIsVerifying(false);
        }
    };

    const handleReject = async () => {
        if (!selectedReason) {
            toast.error('Please select a reason');
            return;
        }

        const finalReason = selectedReason === 'other'
            ? `Other: ${customReason}`
            : rejectionReasons.find(r => r.value === selectedReason)?.label;

        setIsVerifying(true);
        try {
            await onVerify(item.id, false, finalReason);
            toast.success('Identity rejected');
            setShowRejectOptions(false);
            setSelectedReason('');
            setCustomReason('');
        } catch (error) {
            toast.error('Failed to reject');
        } finally {
            setIsVerifying(false);
        }
    };

    return (
        <div className="bg-white border rounded-lg p-4 hover:shadow-md transition-shadow">
            <div className="flex gap-4">
                {/* Left side - ID Image */}
                <div
                    onClick={onClick}
                    className="flex-shrink-0 w-48 h-32 border rounded-lg overflow-hidden cursor-pointer hover:opacity-90 transition-opacity bg-gray-50"
                >
                    <img
                        src={imageUrl}
                        alt="ID Document"
                        className="w-full h-full object-contain"
                    />
                </div>

                {/* Right side - User Info and Actions */}
                <div className="flex-1 flex justify-between">
                    {/* User Details */}
                    <div>
                        <div className="flex items-center gap-2">
                            <h3 className="text-lg font-medium text-gray-900">
                                {item.first_name} {item.last_name}
                            </h3>
                            {renderStatusBadge()}
                        </div>
                        <p className="text-sm text-gray-600">{item.email}</p>
                        <div className="mt-2 space-y-1">
                            <p className="text-xs text-gray-500">
                                <span className="font-medium">Submitted:</span> {new Date(item.id_uploaded_at).toLocaleString()}
                            </p>
                            {item.id_verified_at && (
                                <p className="text-xs text-gray-500">
                                    <span className="font-medium">
                                        {isApproved ? 'Approved:' : 'Rejected:'}
                                    </span> {new Date(item.id_verified_at).toLocaleString()}
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Action Buttons - based on status */}
                    <div className="flex flex-col gap-2 min-w-[140px]">
                        {isPending ? (
                            // Pending - show approve/reject buttons
                            <>
                                <button
                                    onClick={handleApprove}
                                    disabled={isVerifying}
                                    className="flex items-center justify-center px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 disabled:opacity-50"
                                >
                                    {isVerifying ? (
                                        <>
                                            <span className="animate-spin mr-2">⏳</span>
                                            Sending...
                                        </>
                                    ) : (
                                        <>
                                            <Check size={16} className="mr-2" />
                                            Approve
                                        </>
                                    )}
                                </button>

                                {!showRejectOptions ? (
                                    <button
                                        onClick={() => setShowRejectOptions(true)}
                                        disabled={isVerifying}
                                        className="flex items-center justify-center px-4 py-2 border border-red-300 text-red-700 text-sm rounded-lg hover:bg-red-50 disabled:opacity-50"
                                    >
                                        <X size={16} className="mr-2" />
                                        Reject
                                    </button>
                                ) : (
                                   <div className="space-y-2 p-2 bg-gray-50 rounded-lg">
                                        <select
                                            value={selectedReason}
                                            onChange={(e) => setSelectedReason(e.target.value)}
                                            className="w-full px-2 py-1 text-xs border rounded"
                                            autoFocus
                                        >
                                            <option value="">Select reason...</option>
                                            {rejectionReasons.map(r => (
                                                <option key={r.value} value={r.value}>{r.label}</option>
                                            ))}
                                        </select>

                                        {selectedReason === 'other' && (
                                            <input
                                                type="text"
                                                value={customReason}
                                                onChange={(e) => setCustomReason(e.target.value)}
                                                placeholder="Specify reason..."
                                                className="w-full px-2 py-1 text-xs border rounded"
                                            />
                                        )}

                                        <div className="flex gap-1">
                                            <button
                                                onClick={handleReject}
                                                disabled={!selectedReason || (selectedReason === 'other' && !customReason)}
                                                className="flex-1 px-2 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700"
                                            >
                                                Confirm
                                            </button>
                                            <button
                                                onClick={() => {
                                                    setShowRejectOptions(false);
                                                    setSelectedReason('');
                                                    setCustomReason('');
                                                }}
                                                className="px-2 py-1 border rounded text-xs hover:bg-gray-50"
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </>
                        ) : isApproved ? (
                            // Approved - show approved button (disabled)
                            <button
                                disabled
                                className="flex items-center justify-center px-4 py-2 bg-green-100 text-green-700 text-sm rounded-lg cursor-default border border-green-200"
                            >
                                <CheckCircle size={16} className="mr-2" />
                                Approved
                            </button>
                        ) : isRejected ? (
                            // Rejected - show rejected button (disabled)
                            <button
                                disabled
                                className="flex items-center justify-center px-4 py-2 bg-red-100 text-red-700 text-sm rounded-lg cursor-default border border-red-200"
                            >
                                <XCircle size={16} className="mr-2" />
                                Rejected
                            </button>
                        ) : null}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default IdentityItem;